/**
 * Centralized Dynamics 365 Lead Capture Composable
 * 
 * Provides utilities for integrating with Dynamics 365 Marketing Form Capture API.
 * Handles field mappings, data serialization, and form submission for various form types.
 */

declare const d365mktformcapture: any

export type FormType = 'partnerHub' | 'oRing' | 'hydraS' | 'hydraulicSelector' | 'wizard'

export interface FormCaptureConfig {
  FormId: string
  FormApiUrl: string
}

export interface FieldMapping {
  FormFieldName?: string
  DataverseFieldName: string
  DataverseFieldType?: string
  DataverseFieldValue?: string
}

export interface FormCaptureMappingsOptions {
  formType: FormType
  locale?: string
  dynamicValues?: Record<string, any>
  staticGuids?: Record<string, string>
  isLoggedIn?: boolean
  includeUtm?: boolean
  hasMarketingConsent?: boolean
}

export interface BuildSubjectOptions {
  formType: FormType
  context?: {
    partner?: string
    product?: string
    action?: string
  }
}

export interface BuildDescriptionOptions {
  formType?: FormType
  product?: {
    sku?: string
    name?: string
    description?: string
  }
  oring?: {
    designName?: string
    parameters?: Record<string, any>
  }
  hydraulicSelector?: {
    parameters?: Record<string, any>
  }
  partnerHub?: {
    partner?: string
    quantity?: number
    deliveryDate?: string
  }
  customFields?: Record<string, any>
}

// Form IDs for different forms
const FORM_CONFIGS: Record<FormType, FormCaptureConfig> = {
  partnerHub: {
    FormId: 'c5eac0cb-6f90-ef11-8a69-000d3ab85655',
    FormApiUrl: 'https://public-eur.mkt.dynamics.com/api/v1.0/orgs/526b3040-ce06-41d6-8fcd-9101566ddae1/landingpageforms'
  },
  oRing: {
    FormId: '559cf073-1a66-f011-bec3-6045bde08c4b',
    FormApiUrl: 'https://public-eur.mkt.dynamics.com/api/v1.0/orgs/526b3040-ce06-41d6-8fcd-9101566ddae1/landingpageforms'
  },
  hydraS: {
    FormId: '559cf073-1a66-f011-bec3-6045bde08c4b',
    FormApiUrl: 'https://public-eur.mkt.dynamics.com/api/v1.0/orgs/526b3040-ce06-41d6-8fcd-9101566ddae1/landingpageforms'
  },
  hydraulicSelector: {
    FormId: '26e09fdc-7e13-f011-9989-7c1e52fc935b',
    FormApiUrl: 'https://public-eur.mkt.dynamics.com/api/v1.0/orgs/526b3040-ce06-41d6-8fcd-9101566ddae1/landingpageforms'
  },
  wizard: {
    FormId: '2a6d47ac-0108-f111-8406-7ced8d2e0763',
    FormApiUrl: 'https://public-eur.mkt.dynamics.com/api/v1.0/orgs/526b3040-ce06-41d6-8fcd-9101566ddae1/landingpageforms'
  }
}

// Standard GUIDs
const MARKETING_CONSENT_GUID = '61b53cd6-6556-49f1-9217-da244bea7751'
const DEFAULT_COUNTRY_GUID = 'e073a636-08bb-e611-80f4-00155d85c140' // Default country
const EASY_TOPIC_ID = '85bc2bed-571d-f111-8342-0022489eb93b'
const EASY_TOPIC_NAME = 'Easy'
const COMPLIANCE_SETTINGS_ID = '48dc9fab-e231-ee11-bdf4-0022489ea33a'
const MARKETING_CONSENT_LABEL_TEXT = 'I agree that Freudenberg FST GmbH sends me e-mails containing information on related sealing products. You can unsubscribe this consent at any time. At the end of every e-mail you will find a corresponding unsubscribe link.'

export const MARKETING_CONSENT_MAPPING: FieldMapping = {
  DataverseFieldName: 'consentsubmissionvalues',
  DataverseFieldValue: JSON.stringify({
    msdynmkt_purposeid: MARKETING_CONSENT_GUID,
    msdynmkt_topicid: null,
    msdynmkt_consenttypevalue: null,
    channels: 'Email',
    optinwhenchecked: 'true',
    msdynmkt_value: 'Opted In',
    labelText: MARKETING_CONSENT_LABEL_TEXT,
    msdynmkt_compliancesettings4id: COMPLIANCE_SETTINGS_ID
  })
}

// Language ID mapping for Dynamics 365
const LANGUAGE_IDS: Record<string, string> = {
  en: 'a4822068-28d4-e311-80c6-0050568842f2',
  de: 'ffbbe0cf-28d4-e311-80c6-0050568842f2',
  fr: '36dfa3c3-28d4-e311-80c6-0050568842f2',
  it: 'd1c570df-28d4-e311-80c6-0050568842f2',
  es: 'ee9057f6-28d4-e311-80c6-0050568842f2'
}

// Industry codes
export const INDUSTRY_CODES = {
  AUTOMOTIVE: '419260000',
  NON_AUTOMOTIVE: '419260001',
  AUTOMOTIVE_AFTERMARKET: '419260002'
}

// Lead Market codes based on region
export const LEAD_MARKET_CODES = {
  EMEA: '419260004',    // Europe, Middle East, Africa
  NA: '419260005',      // North America
  APAC: '419260003',    // Asia Pacific
  LATAM: '419260006'    // Latin America
}

export const useDynamicsLeadCapture = () => {
  /**
   * Get language ID for a given locale
   * @param locale - Locale code (e.g., 'en', 'de', 'fr')
   * @returns Dynamics 365 language GUID
   */
  const getLanguageId = (locale: string): string => {
    return LANGUAGE_IDS[locale] || LANGUAGE_IDS.en
  }

  /**
   * Get language object for Dynamics 365
   * @param locale - Locale code
   * @returns JSON string with language ID and logical name
   */
  const getLanguageObject = (locale: string): string => {
    return JSON.stringify({
      Id: getLanguageId(locale),
      LogicalName: 'zz_language'
    })
  }

  /**
   * Get country object for Dynamics 365
   * @param countryGuid - Optional country GUID, defaults to standard value
   * @returns JSON string with country ID and logical name
   */
  const getCountryObject = (countryGuid?: string): string => {
    return JSON.stringify({
      Id: countryGuid || DEFAULT_COUNTRY_GUID,
      LogicalName: 'zz_country'
    })
  }

  /**
   * Get lead market code based on current region
   * @param region - Region key (EU, NA, SEA, GLOBAL)
   * @returns Lead market code for Dynamics 365
   */
  const getLeadMarketFromRegion = (region: string): string => {
    const REGION_KEYS = {
      GLOBAL: 'GLOBAL',
      EU: 'EU',
      NA: 'NA',
      SEA: 'SEA'
    }

    const marketMap: Record<string, string> = {
      [REGION_KEYS.NA]: LEAD_MARKET_CODES.NA,
      [REGION_KEYS.EU]: LEAD_MARKET_CODES.EMEA,
      [REGION_KEYS.SEA]: LEAD_MARKET_CODES.APAC,
      [REGION_KEYS.GLOBAL]: LEAD_MARKET_CODES.EMEA
    }

    return marketMap[region] || LEAD_MARKET_CODES.EMEA
  }

  const readUtmCookie = (): Record<string, string> => {
    const empty = {utm_source: '', utm_medium: '', utm_campaign: '', utm_content: ''}
    if (typeof document === 'undefined') {
      return empty
    }
    const match = document.cookie.split('; ').find(row => row.startsWith('gtm_utm='))
    if (!match) {
      return empty
    }
    try {
      const value = decodeURIComponent(match.slice('gtm_utm='.length))
      const parsed = JSON.parse(value)
      return {
        utm_source: parsed.utm_source ?? '',
        utm_medium: parsed.utm_medium ?? '',
        utm_campaign: parsed.utm_campaign ?? '',
        utm_content: parsed.utm_content ?? ''
      }
    } catch {
      return empty
    }
  }

  const buildUtmMappings = (): FieldMapping[] => {
    const {utm_source, utm_medium, utm_campaign, utm_content} = readUtmCookie()
    return [
      {DataverseFieldName: 'zz_utmcampaign', DataverseFieldValue: utm_campaign},
      {DataverseFieldName: 'zz_utmcontent', DataverseFieldValue: utm_content},
      {DataverseFieldName: 'zz_utmsource', DataverseFieldValue: utm_source},
      {DataverseFieldName: 'zz_utmmedium', DataverseFieldValue: utm_medium}
    ]
  }

  /**
   * Build field mappings for form capture based on form type
   * @param options - Configuration options for building mappings
   * @returns Array of field mappings for Dynamics 365
   */
  const buildFormCaptureMappings = (options: FormCaptureMappingsOptions): FieldMapping[] => {
    const {formType, hasMarketingConsent, isLoggedIn = false, includeUtm = true} = options
    const utmMappings = includeUtm ? buildUtmMappings() : []

    // Base mappings common to all forms
    const baseMappings: FieldMapping[] = [
      {
        FormFieldName: 'email',
        DataverseFieldName: 'emailaddress1'
      }
    ]

    // ERP fields for logged-in users
    const erpMappings: FieldMapping[] = isLoggedIn ? [
      {
        FormFieldName: 'easyAccountNumber',
        DataverseFieldName: 'zz_easyaccountnumber'
      },
      {
        FormFieldName: 'easySalesOrganization',
        DataverseFieldName: 'zz_easysalesorganization'
      },
      {
        FormFieldName: 'metadata',
        DataverseFieldName: 'zz_metadata'
      }
    ] : [
      {
        FormFieldName: 'metadata',
        DataverseFieldName: 'zz_metadata'
      }
    ]

    // Industry field only for logged-out users
    const industryMapping: FieldMapping[] = isLoggedIn ? [] : [
      {
        FormFieldName: 'initialIndustry',
        DataverseFieldName: 'zz_initialindustry'
      }
    ]

    switch (formType) {
    case 'partnerHub':
      return [
        {
          FormFieldName: 'firstName',
          DataverseFieldName: 'firstname'
        },
        {
          FormFieldName: 'lastName',
          DataverseFieldName: 'lastname'
        },
        {
          FormFieldName: 'companyName',
          DataverseFieldName: 'companyname'
        },
        ...baseMappings,
        {
          FormFieldName: 'phone',
          DataverseFieldName: 'telephone1'
        },
        {
          FormFieldName: 'comments',
          DataverseFieldName: 'comments'
        },
        {
          FormFieldName: 'description',
          DataverseFieldName: 'description'
        },
        {
          FormFieldName: 'country',
          DataverseFieldName: 'zz_country1'
        },
        {
          FormFieldName: 'marketingLanguage',
          DataverseFieldName: 'zz_marketinglanguage'
        },
        ...industryMapping,
        {
          FormFieldName: 'subject',
          DataverseFieldName: 'subject'
        },
        {
          FormFieldName: 'leadMarket',
          DataverseFieldName: 'zz_leadmarket'
        },
        ...erpMappings,
        ...utmMappings
      ]

    case 'oRing':
    case 'hydraS':
    case 'hydraulicSelector':
      return [
        {
          FormFieldName: 'firstName',
          DataverseFieldName: 'firstname'
        },
        {
          FormFieldName: 'lastName',
          DataverseFieldName: 'lastname'
        },
        {
          FormFieldName: 'company',
          DataverseFieldName: 'companyname'
        },
        ...baseMappings,
        {
          FormFieldName: 'description',
          DataverseFieldName: 'description'
        },
        {
          FormFieldName: 'marketingLanguage',
          DataverseFieldName: 'zz_marketinglanguage'
        },
        {
          FormFieldName: 'country',
          DataverseFieldName: 'zz_country1'
        },
        ...industryMapping,
        {
          FormFieldName: 'leadMarket',
          DataverseFieldName: 'zz_leadmarket'
        },
        ...erpMappings,
        ...utmMappings
      ]

    case 'wizard': {
      // Topic consent - only topicid and topicname are set
      const topicConsent = {
        msdynmkt_purposeid: null,
        msdynmkt_topicid: EASY_TOPIC_ID,
        msdynmkt_topicname: EASY_TOPIC_NAME,
        msdynmkt_consenttypevalue: null,
        channels: 'Email',
        optinwhenchecked: 'true',
        msdynmkt_value: 'Opted In',
        labelText: 'Newsletter',
        msdynmkt_compliancesettings4id: COMPLIANCE_SETTINGS_ID
      }

      const topicConsentMappings = [
        {
          DataverseFieldName: 'consentsubmissionvalues',
          DataverseFieldValue: JSON.stringify(topicConsent)
        }
      ]

      const mapping = [
        {
          FormFieldName: 'firstName',
          DataverseFieldName: 'firstname'
        },
        {
          FormFieldName: 'lastName',
          DataverseFieldName: 'lastname'
        },
        baseMappings[0], // email field
        {
          FormFieldName: 'companyNo',
          DataverseFieldName: 'zz_easyaccountnumber'
        },
        {
          DataverseFieldName: 'zz_easyuser',
          DataverseFieldValue: '1'
        },
        ...utmMappings
      ]

      if (hasMarketingConsent) {
        mapping.push(...topicConsentMappings, MARKETING_CONSENT_MAPPING)
      }

      return mapping
    }

    default:
      return [...baseMappings, ...erpMappings, ...utmMappings]
    }
  }

  /**
   * Build subject line for form submission
   * @param options - Options containing form type and context
   * @returns Subject string for the form
   */
  const buildSubject = (options: BuildSubjectOptions): string => {
    const {formType, context} = options

    switch (formType) {
    case 'partnerHub':
      return 'EASY PartnerHub RFQ'

    case 'oRing':
      return context?.action === 'download'
        ? 'O-Ring Configuration Download Request'
        : 'O-Ring Configuration Email Request'

    case 'hydraS':
      return 'Hydra-S Lead Request'

    case 'hydraulicSelector':
      return 'Hydraulic Selector Inquiry'

    case 'wizard':
      return 'Marketing Opt In'

    default:
      return 'General Inquiry'
    }
  }

  /**
   * Build description/message appendix with product and configuration context
   * @param options - Options containing various context information
   * @returns JSON string or formatted description
   */
  const buildDescriptionAppendix = (options: BuildDescriptionOptions): string => {
    const descriptionParts: Record<string, any> = {}

    // Add Partner Hub specific data
    if (options.partnerHub) {
      if (options.partnerHub.partner) {
        descriptionParts.SelectedPartner = options.partnerHub.partner
      }
      if (options.partnerHub.quantity) {
        descriptionParts.Quantity = options.partnerHub.quantity
      }
      if (options.partnerHub.deliveryDate) {
        descriptionParts.DesiredDeliveryDate = options.partnerHub.deliveryDate
      }
    }

    // Add product information
    if (options.product) {
      const productInfo = [
        options.product.sku,
        options.product.name,
        options.product.description
      ].filter(Boolean).join(' - ')

      if (productInfo) {
        descriptionParts.SelectedProduct = productInfo
      }
    }

    // Add O-Ring specific data
    if (options.oring) {
      if (options.oring.designName) {
        descriptionParts.ORingDesignName = options.oring.designName
      }
      if (options.oring.parameters) {
        descriptionParts.ORingParameters = options.oring.parameters
      }
    }

    // Add Hydraulic Selector data
    if (options.hydraulicSelector?.parameters) {
      descriptionParts.HydraulicSelectorParameters = options.hydraulicSelector.parameters
    }

    // Add any custom fields
    if (options.customFields) {
      Object.assign(descriptionParts, options.customFields)
    }

    return JSON.stringify(descriptionParts)
  }

  /**
   * Load the Dynamics 365 Form Capture script
   * @returns Promise that resolves when script is loaded
   */
  const loadFormCaptureScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (typeof d365mktformcapture !== 'undefined' && d365mktformcapture !== null) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/eur/FormCapture/FormCapture.bundle.js'
      script.async = true

      script.onload = () => {
        resolve()
      }

      script.onerror = () => {
        // eslint-disable-next-line no-console
        console.error('Failed to load Dynamics 365 FormCapture script')
        reject(new Error('Failed to load Dynamics 365 FormCapture script'))
      }

      document.body.appendChild(script)
    })
  }

  /**
   * Load the Dynamics 365 Form Loader script (for embedded forms)
   * @returns Promise that resolves when script is loaded
   */
  const loadFormLoaderScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (document.querySelector('script[src*="FormLoader.bundle.js"]')) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/eur/FormLoader/FormLoader.bundle.js'
      script.async = true

      script.onload = () => {
        resolve()
      }

      script.onerror = () => {
        // eslint-disable-next-line no-console
        console.error('Failed to load Dynamics 365 FormLoader script')
        reject(new Error('Failed to load Dynamics 365 FormLoader script'))
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Get the cached form URL for a specific form type
   * @param formType - Type of form
   * @returns Cached form URL
   */
  const getCachedFormUrl = (formType: FormType): string => {
    const formId = FORM_CONFIGS[formType].FormId
    return `https://assets-eur.mkt.dynamics.com/526b3040-ce06-41d6-8fcd-9101566ddae1/digitalassets/forms/${formId}`
  }

  /**
   * Setup event listener for form capture on a specific form element
   * @param formId - DOM element ID of the form
   * @param formType - Type of form for mapping configuration
   * @param locale - Current locale for language settings
   * @returns Promise that resolves when listener is attached
   */
  const setupFormCaptureListener = async (
    formId: string,
    formType: FormType,
    locale: string = 'en'
  ): Promise<void> => {
    if (typeof d365mktformcapture === 'undefined' || d365mktformcapture === null) {
      // eslint-disable-next-line no-console
      console.warn('d365mktformcapture is not loaded. Call loadFormCaptureScript() first.')
      return
    }

    try {
      const form = await d365mktformcapture.waitForElement('#' + formId)
      const mappings = buildFormCaptureMappings({formType, locale})
      const captureConfig = FORM_CONFIGS[formType]

      form.addEventListener('captureForm', () => {
        const serializedForm = d365mktformcapture.serializeForm(form, mappings)
        const payload = serializedForm.SerializedForm.build()
        d365mktformcapture.submitForm(captureConfig, payload)
      }, true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error setting up form capture listener:', error)
      throw error
    }
  }

  /**
   * Directly capture and submit form data without a physical form element
   * Useful for programmatic form submission
   * @param formType - Type of form
   * @param formData - Form data object
   * @param locale - Current locale
   * @param isLoggedIn - Whether the user is logged in
   * @returns Promise that resolves when submission is complete
   */
  const captureFormData = async (
    formType: FormType,
    formData: Record<string, any>,
    locale: string = 'en',
    isLoggedIn: boolean = false,
    hasMarketingConsent: boolean = true
  ): Promise<void> => {
    if (typeof d365mktformcapture === 'undefined' || d365mktformcapture === null) {
      // eslint-disable-next-line no-console
      console.warn('d365mktformcapture is not loaded')
      return
    }

    try {
      const mappings = buildFormCaptureMappings({formType, locale, isLoggedIn, hasMarketingConsent})

      // Create a virtual form element
      const virtualForm = document.createElement('form')
      for (const [key, value] of Object.entries(formData)) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value !== null && value !== undefined ? String(value) : ''
        virtualForm.appendChild(input)
      }

      const serialized = d365mktformcapture.serializeForm(virtualForm, mappings)
      const payload = serialized.SerializedForm.build()
      const captureConfig = FORM_CONFIGS[formType]

      await d365mktformcapture.submitForm(captureConfig, payload)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('CRM capture error:', error)
    }
  }

  /**
   * Initialize form capture for a specific form type
   * Loads script and sets up listener
   * @param formId - DOM element ID of the form
   * @param formType - Type of form
   * @param locale - Current locale
   * @returns Promise that resolves when initialization is complete
   */
  const initializeFormCapture = async (
    formId: string,
    formType: FormType,
    locale: string = 'en'
  ): Promise<void> => {
    try {
      await loadFormCaptureScript()
      await setupFormCaptureListener(formId, formType, locale)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize form capture:', error)
      throw error
    }
  }

  /**
   * Get form configuration for a specific form type
   * @param formType - Type of form
   * @returns Form capture configuration
   */
  const getFormConfig = (formType: FormType): FormCaptureConfig => {
    return FORM_CONFIGS[formType]
  }

  return {
    // Configuration
    getFormConfig,
    getCachedFormUrl,

    // Language & Locale
    getLanguageId,
    getLanguageObject,
    getCountryObject,
    getLeadMarketFromRegion,

    // Mapping builders
    buildFormCaptureMappings,
    buildSubject,
    buildDescriptionAppendix,

    // Form capture operations
    loadFormCaptureScript,
    loadFormLoaderScript,
    setupFormCaptureListener,
    captureFormData,
    initializeFormCapture,

    // Constants
    INDUSTRY_CODES,
    LEAD_MARKET_CODES
  }
}
